import React, { useState, useEffect, useCallback } from "react";
import SideBar from "../components/SideBar";
import Breadcrumbs from "../components/Breadcrumbs";
import SearchBar from "../components/SearchBar";
import UserIcon from "../components/UserIcon";
import Footer from "../components/Footer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCirclePlus,
  faPenToSquare,
  faTrash,
  faBullhorn,
  faRotateRight,
  faCheckCircle,
  faTimesCircle,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { useSelector, useDispatch } from "react-redux";
import { selectUser } from "../redux/slices/userSlice";
import { selectSearch, setSearch } from "../redux/slices/itemSlice";
import toast, { Toaster } from "react-hot-toast";
import AnnouncementsService from "../services/announcements.service";
import AnnouncementModal from "../components/Modals/AnnouncementModal";
import DeleteAnnouncementModal from "../components/Modals/DeleteAnnouncementModal";

function Announcements() {
  const dispatch = useDispatch();
  const searchInput = useSelector(selectSearch);
  const currentUser = useSelector(selectUser);
  const adminRole = currentUser?.user?.[1];
  const canEdit = ["Admin", "DataEntry"].includes(adminRole) || !adminRole;

  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Modals state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  // Clear search on mount
  useEffect(() => {
    dispatch(setSearch(""));
  }, [dispatch]);

  // Fetch announcements from API
  const fetchAnnouncements = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      const data = await AnnouncementsService.getAll(currentUser);
      setAnnouncements(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching announcements:", error);
      // Fallback: If getAll required auth or failed, try getActive
      try {
        const activeData = await AnnouncementsService.getActive();
        if (Array.isArray(activeData)) {
          setAnnouncements(activeData);
          toast("Announcements loaded successfully", { icon: "ℹ️" });
          return;
        }
      } catch (e) {
        console.error("Fallback to active announcements also failed:", e);
      }
      toast.error("Failed to load announcements from server.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  // Filter announcements by search query
  const filteredAnnouncements = announcements.filter((item) => {
    if (!searchInput) return true;
    const query = searchInput.toLowerCase().trim();
    const textAr = (item.text_ar || "").toLowerCase();
    const textEn = (item.text_en || "").toLowerCase();
    return textAr.includes(query) || textEn.includes(query);
  });

  // Handle toggle status
  const handleToggleStatus = async (item) => {
    if (!canEdit) return;
    setTogglingId(item.id);
    const newStatus = !item.isActive;

    // Optimistic UI update
    setAnnouncements((prev) =>
      prev.map((a) => (a.id === item.id ? { ...a, isActive: newStatus } : a))
    );

    try {
      // Try toggle endpoint first
      try {
        await AnnouncementsService.toggleStatus(item.id, currentUser);
      } catch (err) {
        // Fallback to updateStatus endpoint
        await AnnouncementsService.updateStatus(item.id, newStatus, currentUser);
      }
      toast.success(
        newStatus
          ? "Announcement activated successfully"
          : "Announcement deactivated successfully"
      );
    } catch (error) {
      console.error("Error toggling status:", error);
      // Revert optimistic update
      setAnnouncements((prev) =>
        prev.map((a) => (a.id === item.id ? { ...a, isActive: item.isActive } : a))
      );
      toast.error("Failed to update announcement status");
    } finally {
      setTogglingId(null);
    }
  };

  // Handle delete
  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      setDeleteLoading(true);
      await AnnouncementsService.delete(itemToDelete.id, currentUser);
      setAnnouncements((prev) => prev.filter((a) => a.id !== itemToDelete.id));
      toast.success("Announcement deleted successfully");
      setDeleteModalOpen(false);
      setItemToDelete(null);
    } catch (error) {
      console.error("Error deleting announcement:", error);
      toast.error("Failed to delete announcement");
    } finally {
      setDeleteLoading(false);
    }
  };

  const activeCount = announcements.filter((a) => a.isActive).length;

  return (
    <div className="w-[96%] text-[17px] m-auto mt-6">
      <Toaster position="top-center" />

      <div className="m-auto mt-6 flex">
        {/* Sidebar */}
        <div>
          <SideBar />
        </div>

        {/* Main Content Area */}
        <div className="w-full flex flex-col ml-6 text-[#033362] font-semibold">
          {/* Top Navbar: Breadcrumbs + SearchBar + UserIcon */}
          <div className="flex justify-between items-center w-full">
            <Breadcrumbs
              breads={[
                {
                  title: "Announcements",
                  link: "/announcements",
                },
              ]}
            />
            <div className="flex items-center h-8 w-[35%] gap-3">
              <SearchBar placeholder="Search announcements..." />
              <UserIcon />
            </div>
          </div>

          {/* Content Card */}
          <div className="bg-white grow mt-2 rounded-xl shadow-[0px_0px_16px_rgb(210,215,211)] p-6 min-h-[620px] flex flex-col">
            {/* Header Section */}
            <div className="flex flex-wrap items-center justify-between gap-4 pb-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#0388CC]/10 text-[#0388CC] flex items-center justify-center text-xl shadow-sm">
                  <FontAwesomeIcon icon={faBullhorn} />
                </div>
                <div>
                  <h1 className="text-[20px] font-bold text-[#033362]">
                    Announcements
                  </h1>
                  <p className="text-[12px] text-gray-500 font-normal">
                    Manage website header top bar announcements banner
                  </p>
                </div>
              </div>

              {/* Stats & Actions */}
              <div className="flex items-center gap-3">
                {/* Stats Chips */}
                <div className="hidden sm:flex items-center gap-2 text-xs font-medium">
                  <span className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700">
                    Total: <strong className="text-[#033362]">{announcements.length}</strong>
                  </span>
                  <span className="px-3 py-1.5 rounded-lg bg-emerald-50 text-[#007530]">
                    Active: <strong>{activeCount}</strong>
                  </span>
                </div>

                {/* Refresh Button */}
                <button
                  onClick={() => fetchAnnouncements(true)}
                  disabled={refreshing}
                  title="Refresh"
                  className="w-10 h-10 rounded-xl border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-600 transition-colors disabled:opacity-50"
                >
                  <FontAwesomeIcon
                    icon={faRotateRight}
                    className={refreshing ? "animate-spin" : ""}
                  />
                </button>

                {/* Add Announcement Button */}
                {canEdit && (
                  <button
                    onClick={() => {
                      setEditingItem(null);
                      setModalOpen(true);
                    }}
                    className="flex items-center gap-2 bg-[#0388CC] hover:bg-[#026c9f] transition-all text-white px-4 py-2.5 rounded-xl font-medium text-sm shadow-md shadow-[#0388CC]/20 cursor-pointer"
                  >
                    <FontAwesomeIcon icon={faCirclePlus} />
                    <span>New Announcement</span>
                  </button>
                )}
              </div>
            </div>

            {/* Table Section */}
            <div className="grow mt-4 overflow-x-auto">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl mb-3 text-[#0388CC]" />
                  <p className="text-sm font-medium">Loading announcements...</p>
                </div>
              ) : filteredAnnouncements.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center text-gray-400">
                  <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center text-2xl text-gray-300 mb-3">
                    <FontAwesomeIcon icon={faBullhorn} />
                  </div>
                  <h3 className="text-base font-bold text-gray-700 mb-1">
                    {searchInput ? "No matching announcements" : "No announcements found"}
                  </h3>
                  <p className="text-xs text-gray-400 max-w-sm mb-4">
                    {searchInput
                      ? "Try searching with different keywords or clear the search field."
                      : "Create a new announcement banner to display at the top of your website."}
                  </p>
                  {canEdit && !searchInput && (
                    <button
                      onClick={() => {
                        setEditingItem(null);
                        setModalOpen(true);
                      }}
                      className="px-4 py-2 rounded-xl bg-[#0388CC] text-white text-xs font-semibold hover:bg-[#026c9f] transition-all"
                    >
                      + Add First Announcement
                    </button>
                  )}
                </div>
              ) : (
                <table className="w-full text-start border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 text-xs font-bold text-[#033362] uppercase tracking-wider bg-gray-50/70">
                      <th className="py-3 px-4 text-center w-16">#</th>
                      <th className="py-3 px-4 text-center w-20">Order</th>
                      <th className="py-3 px-4 text-right">Arabic Text</th>
                      <th className="py-3 px-4 text-left">English Text</th>
                      <th className="py-3 px-4 text-center w-28">Status</th>
                      {canEdit && (
                        <th className="py-3 px-4 text-center w-24">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm font-normal">
                    {filteredAnnouncements.map((item, index) => {
                      const isToggling = togglingId === item.id;
                      return (
                        <tr
                          key={item.id || index}
                          className="hover:bg-blue-50/30 transition-colors"
                        >
                          {/* ID */}
                          <td className="py-3.5 px-4 text-center text-gray-500 font-mono text-xs">
                            {item.id}
                          </td>

                          {/* Order */}
                          <td className="py-3.5 px-4 text-center">
                            <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                              {item.order ?? 1}
                            </span>
                          </td>

                          {/* Arabic Text */}
                          <td className="py-3.5 px-4 text-right font-arabic font-medium text-gray-800 leading-relaxed max-w-xs">
                            {item.text_ar || (
                              <span className="text-gray-300 italic text-xs">
                                No Arabic text
                              </span>
                            )}
                          </td>

                          {/* English Text */}
                          <td className="py-3.5 px-4 text-left font-sans text-gray-700 leading-relaxed max-w-xs">
                            {item.text_en || (
                              <span className="text-gray-300 italic text-xs">
                                No English text
                              </span>
                            )}
                          </td>

                          {/* Status toggle */}
                          <td className="py-3.5 px-4 text-center">
                            <button
                              onClick={() => handleToggleStatus(item)}
                              disabled={!canEdit || isToggling}
                              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                                item.isActive
                                  ? "bg-emerald-100/70 text-[#007530] hover:bg-emerald-200/70"
                                  : "bg-red-100/70 text-red-600 hover:bg-red-200/70"
                              } ${!canEdit ? "cursor-default" : "cursor-pointer"}`}
                            >
                              {isToggling ? (
                                <FontAwesomeIcon
                                  icon={faSpinner}
                                  className="animate-spin text-xs"
                                />
                              ) : (
                                <FontAwesomeIcon
                                  icon={
                                    item.isActive
                                      ? faCheckCircle
                                      : faTimesCircle
                                  }
                                  className="text-xs"
                                />
                              )}
                              <span>
                                {item.isActive ? "Active" : "Inactive"}
                              </span>
                            </button>
                          </td>

                          {/* Actions */}
                          {canEdit && (
                            <td className="py-3.5 px-4 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => {
                                    setEditingItem(item);
                                    setModalOpen(true);
                                  }}
                                  title="Edit Announcement"
                                  className="w-8 h-8 rounded-lg bg-[#0388CC]/10 text-[#0388CC] hover:bg-[#0388CC] hover:text-white transition-all flex items-center justify-center text-xs"
                                >
                                  <FontAwesomeIcon icon={faPenToSquare} />
                                </button>
                                <button
                                  onClick={() => {
                                    setItemToDelete(item);
                                    setDeleteModalOpen(true);
                                  }}
                                  title="Delete Announcement"
                                  className="w-8 h-8 rounded-lg bg-red-50 text-red-600 hover:bg-red-600 hover:text-white transition-all flex items-center justify-center text-xs"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Bottom info footer */}
            <div className="pt-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
              <span>Active banner announcements management</span>
              <span>
                Showing {filteredAnnouncements.length} of {announcements.length} announcements
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Add / Edit Announcement Modal */}
      <AnnouncementModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingItem(null);
        }}
        initialData={editingItem}
        totalCount={announcements.length}
        onSuccess={() => fetchAnnouncements(false)}
      />

      {/* Delete Confirmation Modal */}
      <DeleteAnnouncementModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
        item={itemToDelete}
      />

      <Footer />
    </div>
  );
}

export default Announcements;
